import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ArrowRight } from 'lucide-react';
import logo from '../assets/logo.png';
import { prefersReduced, smooth } from './home/v3/motion';
import { portalToElement } from './site/Portal';

const NAV = [
  { to: '/nosso-trabalho', label: 'Nosso Trabalho' },
  { to: '/sobre', label: 'Sobre' },
  { to: '/contato', label: 'Contato' },
];

function Header() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Fecha o menu a cada troca de rota (o portal intercepta os cliques nos links).
  useEffect(() => setMenuOpen(false), [location.pathname]);

  // Transparente no topo, com fundo ao rolar: sentinela no topo, sem escutar scroll.
  useEffect(() => {
    const sentinel = document.getElementById('page-top');
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting));
    io.observe(sentinel);
    return () => io.disconnect();
  }, []);

  // Menu de tela cheia no celular: trava o scroll e os links entram em cascata.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle('menu-open', menuOpen);
    if (smooth.lenis) {
      if (menuOpen) smooth.lenis.stop();
      else smooth.lenis.start();
    }
    if (menuOpen && menuRef.current && !prefersReduced()) {
      gsap.fromTo(
        menuRef.current.querySelectorAll('.mnav-item, .mnav-foot'),
        { yPercent: 110, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.8, ease: 'expo.out', stagger: 0.06, delay: 0.12 }
      );
    }
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    if (menuOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const tab = menuOpen ? 0 : -1;
  const isHome = location.pathname === '/';
  const goToWheel = (e) => {
    if (!document.getElementById('comecar')) return;
    e.preventDefault();
    portalToElement('#comecar', e);
  };

  return (
    <header className={`site-header site-header--v3 ${scrolled ? 'is-scrolled' : ''} ${menuOpen ? 'is-menu' : ''}`}>
      <nav className="site-nav" aria-label="Principal">
        <Link to="/" className="logo-link" aria-label="Idealizer, página inicial">
          <img src={logo} alt="Idealizer" className="logo-image" width="460" height="216" />
        </Link>

        <ul className="nav-list">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link to={n.to} className={`nav-item ${isActive(n.to) ? 'active' : ''}`}>
                {n.label}
              </Link>
            </li>
          ))}
        </ul>

        <Link
          to="/contato"
          className="nav-cta"
          // Na Home, a roda de "Começar um projeto" está na própria página: vai até ela pelo portal.
          data-no-portal={isHome ? '' : undefined}
          onClick={isHome ? goToWheel : undefined}
        >
          Começar um projeto
          <ArrowRight size={16} strokeWidth={2} aria-hidden />
        </Link>

        <button
          type="button"
          className={`hamburger ${menuOpen ? 'active' : ''}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-menu"
        >
          <span />
          <span />
        </button>
      </nav>

      <div id="mobile-menu" className={`mnav ${menuOpen ? 'is-open' : ''}`} ref={menuRef} aria-hidden={!menuOpen}>
        <ul>
          {[{ to: '/', label: 'Início' }, ...NAV].map((n) => (
            <li key={n.to} className="mnav-row">
              <Link to={n.to} className={`mnav-item ${location.pathname === n.to ? 'active' : ''}`} tabIndex={tab} onClick={() => setMenuOpen(false)}>
                {n.label}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mnav-foot">
          <Link to="/contato" className="hv3-btn hv3-btn--primary" tabIndex={tab}>
            <span className="hv3-btn-inner">
              Começar um projeto <ArrowRight size={18} strokeWidth={2} aria-hidden />
            </span>
          </Link>
          <a href="mailto:contato@idealizer.com.br" className="mnav-mail" tabIndex={tab}>
            contato@idealizer.com.br
          </a>
        </div>
      </div>
    </header>
  );
}

export default Header;
